import { useEffect, useState } from "react";
import { Loader2, Receipt as ReceiptIcon, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const money = (c, v) => `${c || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const badge = (s) => s === "issued"
  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
  : "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400";
const kindBadge = (k) => k === "settlement"
  ? "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold"
  : "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300";

export default function Payments() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/finance/payment_receipt")).data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const issue = async (rid) => {
    setBusy(true);
    try { await api.post(`/finance/payment_receipt/${rid}/issue`, {}); toast.success("Receipt issued"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openDetail = async (id) => {
    try { setDetail((await api.get(`/finance/payment_receipt/${id}`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Payments & Receipts</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Payments</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Deposit receipts anchor to the PI (no Booking Number needed); settlement receipts anchor to the Invoice.</p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="payments-empty-state">
          <ReceiptIcon className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No receipts yet</p>
          <p className="mt-1 text-xs text-slate-400">Create a deposit receipt from a verified Pro Forma Invoice.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Receipt #</th>
                  <th className="px-5 py-3 font-medium">Kind</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Booking #</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`receipt-row-${r.id}`}>
                    <td className="cursor-pointer px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold" onClick={() => openDetail(r.id)}>{r.document_number || "— draft —"}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${kindBadge(r.receipt_kind)}`}>{r.receipt_kind}</span></td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.booking_number || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.paid_amount)}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(r.status)}`}>{r.status}</span></td>
                    <td className="px-5 py-3 text-right">
                      {r.status === "draft" && hasPermission("payments.create") && (
                        <button onClick={() => issue(r.id)} disabled={busy} data-testid={`payment-issue-${r.id}`} className="rounded-md bg-ink-gold px-2.5 py-1 text-[11px] font-semibold text-[#0B1120] disabled:opacity-50">Issue</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && <ReceiptDetail r={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ReceiptDetail({ r, onClose }) {
  const rel = r.related || {};
  const R = ({ label, children }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-ink-line">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-800 dark:text-ink-text">{children}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="receipt-detail-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Receipt · {r.receipt_kind}</p>
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-ink-text">{r.document_number || "— Draft —"}</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-sm">
          <R label="Customer">{r.customer_name || "—"}</R>
          <R label="Status">{r.status}</R>
          <R label="Amount">{money(r.currency, r.paid_amount)}</R>
          <R label="Payment reference">{r.payment_reference || "—"}</R>
          <R label="Payment method">{r.payment_method || "—"}</R>
          <R label="Pro Forma Invoice">{rel.proforma_invoice?.document_number || "—"}</R>
          <R label="Booking #">{r.booking_number || rel.booking?.booking_number || "—"}</R>
          <R label="Notes">{r.notes || "—"}</R>
        </div>
      </div>
    </div>
  );
}
